import { AccessForbiddenPage } from '@openx/components-core/lib/AccessForbiddenBackPage/AccessForbiddenPage';
import { PageHeader } from '@openx/components-header/PageHeader';
import { OPENX_AGENT_TOOL } from '@openx/components-openx-agent/components/Home/utils/consts';
import { ToolCallButton } from '@openx/components-openx-agent/components/ToolCallButton/ToolCallButton';
import { PackageCreateForm } from '@openx/components-package/components/PackageForm/PackageCreateForm';
import { PackageEditForm } from '@openx/components-package/components/PackageForm/PackageEditForm';
import type { PackageRouteParams } from '@openx/components-package/types';
import { usePackageContext } from '@openx/components-package/utils/context/PackageContext';
import { ACL_TYPE } from '@openx/types/aclType';
import type { PackageFormState } from '@openx/types/package';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.js';
import { isAllowedByCompiledAcl } from '@openx/utils/permission/validators';
import { type JSX, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { useAuth } from '../../../components/AuthContext/context';

export const PackageFormPage = (): JSX.Element | null => {
  const { t } = useTranslation();
  const { useIsAllowed } = usePermissionContext();
  const { isAllowed } = useIsAllowed();

  const { session } = useAuth();
  const navigate = useNavigate();

  const { isOaSuborg, usePackageFetch } = usePackageContext();

  const { packageId = '' } = useParams<PackageRouteParams>();
  const { pckg, pckgLoading } = usePackageFetch(packageId);

  const accountUid = useMemo(() => session?.exchange_session?.account?.uid ?? null, [session]);

  const isEditForbidden = isOaSuborg && !pckgLoading && pckg?.account_uid !== accountUid;

  const checkTargetingButton = useCallback(
    (isSubmitting: boolean, formValues: PackageFormState) => {
      return (
        <ToolCallButton
          isAllowed={isAllowed(isAllowedByCompiledAcl(ACL_TYPE.XPERT_ENABLED))}
          isLoading={isSubmitting}
          toolId={OPENX_AGENT_TOOL.TARGETING_EXPLAINER}
          toolData={formValues}
          dataTest="check-targeting"
          name={t('Check Targeting')}
        />
      );
    },
    [t, isAllowed]
  );

  if (!accountUid) {
    return null;
  }

  if (packageId) {
    if (isEditForbidden) return <AccessForbiddenPage />;

    return (
      <PackageEditForm
        packageId={packageId}
        goBack={() => {
          navigate(-1);
        }}
        titlePrefix={'OpenXSelect'}
        openXAgentTools={checkTargetingButton}
      />
    );
  }

  return (
    <PageHeader title={t('Create Package')} titlePrefix="OpenXSelect">
      <PackageCreateForm
        accountUid={accountUid}
        goBack={() => {
          navigate(-1);
        }}
        openXAgentTools={checkTargetingButton}
      />
    </PageHeader>
  );
};
